import {
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
} from "@chakra-ui/react";
import { CloseCircleIcon } from "@root/assets";
import { DownArrow } from "@root/assets/icons/downArrow";
import useOnClickOutside from "@root/hooks/useOnclickoutside";
import { testId as testIdFn } from "@root/utils/e2e";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker, {
  CalendarProps,
  DateObject,
  DatePickerProps,
} from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import "./style.css";
import { CalenderIcon } from "@root/assets/icons";

const Size = {
  SM: "sm",
  MD: "md",
  LG: "lg",
};

interface IDateField {
  testID?: string;
  size?: (typeof Size)[keyof typeof Size];
  isISO?: boolean;
  timePicker?: boolean;
  timePickerPosition?: string;
  onClear?: () => void;
  onChange?: (e: string | string[]) => void;
}

export const DateField: React.FC<
  DatePickerProps & CalendarProps & IDateField & allAnyTypes
> = ({
  size = Size.MD,
  zIndex = 1,
  testID = "",
  placeholder = "Select Date",
  format = "DD/MM/YYYY",
  onOpenPickNewDate = false,
  highlightToday = false,
  value,
  editable = false,
  disabled = false,
  isISO = false,
  timePicker = false,
  timePickerPosition = "",
  inputWidth,
  width, // allow width prop
  portal = false,
  onChange,
  onClear,
  plugins,
  height,
  ...props
}) => {
  const datePickerRef = useRef<any>();
  const [selectedValue, setSelectedValue] = useState<any>(value);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  // Tracks whether the last close was triggered by a completed range selection
  // (vs user dismissing the calendar mid-selection).
  const justCompletedRef = useRef(false);
  const dir = "ltr";
  const isRtl = false;

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const handledeliveryDatePickerClose = useCallback(() => {
    if (!portal && datePickerRef.current) {
      datePickerRef.current.closeCalendar();
    }
  }, [datePickerRef]);

  useOnClickOutside({
    ref: datePickerRef,
    handler: handledeliveryDatePickerClose,
  });

  const formatDate = (date: DateObject, endDate?: boolean) => {
    if (isISO) {
      const updatedDate = date.toDate();

      if (!timePicker) {
        endDate
          ? updatedDate.setHours(23, 59, 59, 0)
          : updatedDate.setHours(0, 0, 0, 0);
      }

      const newDate = new Date(updatedDate);
      // newDate.setTime(newDate.getTime() - newDate.getTimezoneOffset() * 60 * 1000);
      return newDate.toISOString();
    }

    return date.format(format);
  };

  const isoToDate = (date: any) => {
    const newDate = new Date(date.toString());
    // newDate.setTime(newDate.getTime() + newDate.getTimezoneOffset() * 60 * 1000);
    return newDate;
  };

  const handleDateChange = (e: DateObject | DateObject[] | null) => {
    let date: string | string[] = "";

    if (Array.isArray(e)) {
      date = [];
      if (e[0]) date.push(formatDate(e[0]));

      if (e[1]) {
        date.push(formatDate(e[1], true));
        justCompletedRef.current = true;
        if (!timePicker && datePickerRef.current)
          datePickerRef.current.closeCalendar();
      }
    } else if (e !== null) {
      date = formatDate(e);
    }

    setSelectedValue(e);
    onChange?.(date);
  };

  // When the calendar opens, the DatePicker receives value={undefined} so it
  // starts with a clean slate — prevents the first click from "deselecting"
  // an existing range instead of starting a new selection.
  const handleOpen = useCallback(() => {
    setIsPickerOpen(true);
  }, []);

  // When the calendar closes, sync the display back to the last confirmed value.
  // If the close was triggered by completing a range selection we skip the sync
  // (the parent will update `value` and the useEffect below will handle it).
  const handleClose = useCallback(() => {
    if (!justCompletedRef.current) {
      // User dismissed without completing — restore display to confirmed value.
      if (isISO) {
        if (Array.isArray(value)) {
          setSelectedValue([
            value[0] ? isoToDate(value[0]) : undefined,
            value[1] ? isoToDate(value[1]) : undefined,
          ]);
        } else if (value) {
          setSelectedValue(isoToDate(value));
        } else {
          setSelectedValue(undefined);
        }
      } else {
        setSelectedValue(value ?? undefined);
      }
    }
    justCompletedRef.current = false;
    setIsPickerOpen(false);
  }, [value, isISO]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isISO) {
      if (Array.isArray(value)) {
        setSelectedValue([
          value[0] ? isoToDate(value[0]) : undefined,
          value[1] ? isoToDate(value[1]) : undefined,
        ]);
      } else if (value) {
        setSelectedValue(isoToDate(value));
      } else {
        setSelectedValue(undefined);
      }
    }
  }, [value, isISO]);

  const handleCalendarOpen = () => {
    const { openCalendar, isOpen } = datePickerRef.current;
    if (!isOpen) openCalendar();
  };

  const handleReset = () => {
    setSelectedValue(undefined);
    onClear?.();
  };

  const startDate = `${
    monthNames?.[new Date(selectedValue?.[0]).getMonth()]
  } ${new Date(selectedValue?.[0]).getDate()} - `;
  const endDate = `${
    monthNames?.[new Date(selectedValue?.[1]).getMonth()]
  } ${new Date(selectedValue?.[1]).getDate()}, ${new Date(
    selectedValue?.[1]
  ).getFullYear()}`;
  const { t } = useTranslation("common");

  return (
    <InputGroup
      zIndex={1}
      display={"flex"}
      alignItems={"center"}
      size={size}
      dir={dir}
      width={width || inputWidth}
    >
      <DatePicker
        className="custom-calendar"
        containerStyle={{
          width: "100%",
          visibility: "hidden",
        }}
        zIndex={1}
        portal={portal}
        ref={datePickerRef}
        {...(testID ? testIdFn(testID) : {})}
        render={
          <Input
            autoComplete="off"
            dir={dir}
            {...(isRtl
              ? { pl: size === Size.SM ? "32px" : "40px" }
              : { pr: size === Size.SM ? "32px" : "40px" })}
            size={size}
            isDisabled={disabled}
            height={height || "44px"}
            border={"1px solid #CDD6DC"}
            borderRadius={"12px"}
            placeholder={placeholder}
            {...(testID
              ? testIdFn(
                  `${testID}-input${selectedValue ? `-${selectedValue}` : ""}`
                )
              : {})}
          />
        }
        placeholder={placeholder}
        editable={editable}
        format={format}
        onOpenPickNewDate={onOpenPickNewDate}
        highlightToday={highlightToday}
        value={isPickerOpen ? undefined : value}
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={handleDateChange}
        plugins={
          plugins
            ? plugins
            : timePicker
            ? [<TimePicker key={0} position={timePickerPosition} />]
            : undefined
        }
        {...props}
      />
      <Flex
        position={"absolute"}
        py={"5px"}
        alignItems={"center"}
        height={"100%"}
        width={"100%"}
        pl={"16px"}
        onClick={handleCalendarOpen}
        border={"1px solid"}
        h={"44px"}
        pt={"4px"}
        borderColor={"#CDD6DC"}
        _dark={{
          borderColor: "gray.600",
        }}
        borderRadius={"40px"}
      >
        <Text color={"#5C6670"}>
          {!selectedValue?.[0] && !selectedValue?.[1]
            ? placeholder || t("translate.selectDate")
            : `${selectedValue?.[0] ? startDate : ""} ${
                selectedValue?.[1] ? endDate : ""
              }`}
        </Text>
      </Flex>
      <InputRightElement height={height || "44px"}>
        <IconButton
          height={height || "44px"}
          aria-label="datefieldbtn"
          _hover={{ background: "transparent" }}
          variant="ghost"
          size={size}
          isDisabled={disabled}
          onClick={selectedValue && onClear ? handleReset : handleCalendarOpen}
          icon={
            selectedValue && onClear ? (
              <CloseCircleIcon color="#0A293B" />
            ) : (
              <CalenderIcon />
            )
          }
        />
      </InputRightElement>
    </InputGroup>
  );
};
